import type { AssetSheetCellFormat, AssetSheetColumnMapEntryDTO } from '@atlas/types'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Button, IconButton, Tooltip, TooltipContent } from '@atlas/ui-primitives'
import { Info } from 'lucide-react'
import { COLUMN_MAP_HEADINGS, type ColumnMapHeading } from './column-map-headings'

export interface ColumnMapTableProps {
	columns: AssetSheetColumnMapEntryDTO[]
	onEdit: (column: AssetSheetColumnMapEntryDTO) => void
}

/** A separator or ignored part as the admin reads it: whitespace by name, anything else as typed. */
export const describeToken = (token: string) => ({ '\n': 'line break', ' ': 'space', '\t': 'tab' })[token] ?? token

/** A token in running text: whitespace by name, anything else in quotes, so a comma separator reads as one. */
const quoteToken = (token: string) => (describeToken(token) === token ? `"${token}"` : describeToken(token))

/** One tab's mapped columns, with what each heading means one hover away. */
export function ColumnMapTable({ columns, onEdit }: ColumnMapTableProps) {
	return (
		<div className="flex flex-col gap-ds-3">
			<HowItWorks />
			<div className="overflow-x-auto rounded-ds-card border border-ds-border bg-ds-surface-elevated">
				<Table className="min-w-3xl">
					<TableHeader>
						<TableRow>
							{COLUMN_MAP_HEADINGS.map((h) => (
								<TableHead key={h.title}>
									<HeadingWithInfo heading={h} />
								</TableHead>
							))}
							<TableHead>
								<span className="sr-only">Actions</span>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{columns.map((c) => (
							<TableRow key={c.id}>
								<TableCell className="text-ds-sm text-ds-text">{c.columnName}</TableCell>
								<TableCell className="font-ds-mono text-ds-xs text-ds-text">{c.axis}</TableCell>
								<TableCell className="text-ds-sm">
									{c.value ? (
										<span className="text-ds-text">{c.value.label}</span>
									) : (
										<span className="text-ds-text-muted">From the cell</span>
									)}
								</TableCell>
								<TableCell className="text-ds-sm text-ds-text">
									{c.level ?? <span className="text-ds-text-muted">—</span>}
								</TableCell>
								<TableCell className="text-ds-sm text-ds-text">
									<Reading format={c.cellFormat} />
								</TableCell>
								<TableCell className="text-right">
									<Button variant="secondary" size="sm" onPress={() => onEdit(c)} aria-label={`Edit ${c.columnName}`}>
										Edit
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<p className="text-ds-xs text-ds-text-muted">
				Adding or removing a column, or changing its axis, is a seed change and cannot be done here.
			</p>
		</div>
	)
}

function HeadingWithInfo({ heading }: { heading: ColumnMapHeading }) {
	return (
		<span className="inline-flex items-center gap-ds-1">
			{heading.title}
			<Tooltip>
				<IconButton
					variant="muted"
					size="sm"
					className="h-5 w-5 rounded-ds-full"
					aria-label={`What ${heading.title} means`}
				>
					<Info aria-hidden className="h-3 w-3" />
				</IconButton>
				<TooltipContent className="max-w-xs normal-case">
					{heading.meaning} {heading.change}
				</TooltipContent>
			</Tooltip>
		</span>
	)
}

function HowItWorks() {
	return (
		<details open className="rounded-ds-card border border-ds-border bg-ds-surface-elevated px-ds-4 py-ds-3">
			<summary className="cursor-pointer text-ds-sm font-medium text-ds-text">How the column map works</summary>
			<div className="mt-ds-3 flex max-w-3xl flex-col gap-ds-3 text-ds-sm text-ds-text">
				<p>
					Each line is one column of the workbook that the sync reads; columns not listed are not read. Rows are
					identified by <code className="font-ds-mono">#</code>, and only rows whose Status is{' '}
					<code className="font-ds-mono">ready</code> are read.
				</p>
				<dl className="grid gap-x-ds-4 gap-y-ds-2 sm:grid-cols-[8rem_minmax(0,1fr)]">
					{COLUMN_MAP_HEADINGS.map((h) => (
						<div key={h.title} className="contents">
							<dt className="font-medium">{h.title}</dt>
							<dd>
								{h.meaning} <span className="text-ds-text-muted">{h.change}</span>
							</dd>
						</div>
					))}
				</dl>
				<p>
					<span className="font-medium">To change a column</span>, click Edit. The change applies to this tab only,
					unless you tick "Apply to every tab". It takes effect on the next sync, which reads every row of the changed
					tabs again, and Recent activity lists it with who made it.
				</p>
			</div>
		</details>
	)
}

function Reading({ format }: { format: AssetSheetCellFormat }) {
	const parts: string[] = [format.required ? 'required' : 'optional']
	if (format.rootsOnly) parts.push('brands only')
	if (format.parentColumn) parts.push(`under ${format.parentColumn}`)
	if (format.split?.length) parts.push(`split on ${format.split.map(quoteToken).join(' or ')}`)
	if (format.ignore?.length) parts.push(`ignore ${format.ignore.map(quoteToken).join(' or ')}`)
	return <span>{parts.join(' · ')}</span>
}
